import { DataManager, Query } from '@syncfusion/ej2-data';
import { FilteringEventArgs } from '@syncfusion/ej2-react-dropdowns';
import debounce from 'lodash/debounce';
import { EMPTY, map, of, switchMap } from 'rxjs';
import { ajax } from 'rxjs/ajax';

import { notificationService } from 'subjects/common/NotificationService';

import Auth from '../Auth';

type ODataResponse = {
    '@odata.context': string;
};

export type ODataArrayResponse<T> = {
    '@odata.count': string;
    value: T[];
} & ODataResponse;

export type ODataSingleResponse<T> = ODataResponse & T;

function fromAuthToken() {
    if (Auth.isExpired()) {
        notificationService.error({
            title: 'Session Expired',
            content: 'Please login again to re-authorize',
        });

        return EMPTY;
    }

    return of(`Bearer ${Auth.getToken()}`);
}

export function getODataObject<T>(url) {
    return fromAuthToken().pipe(
        switchMap((header) =>
            ajax<ODataSingleResponse<T>>({
                url,
                method: 'GET',
                responseType: 'json',
                headers: {
                    Authorization: header,
                },
            }),
        ),
        map((response) => response.response),
    );
}

export function getODataArray<T>(url) {
    return getODataArrayResponse<T>(url).pipe(
        map((odataResponse) => odataResponse.value)
    );
}

export function getODataArrayResponse<T>(url) {
    return fromAuthToken().pipe(
        switchMap((header) =>
            ajax<ODataArrayResponse<T>>({
                url,
                method: 'GET',
                responseType: 'json',
                headers: {
                    Authorization: header,
                },
            }),
        ),
        map((response) => response.response),
    );
}

/***
 * Replace `+` in a DateTimeOffset with `%2B`
 * Add any character is not well interprated and need to be replace it.
 */
export function encodeURI(uri) {
    return uri.replace(/\+/g, '%2B');
}

/*** Debounced filtering for DataManager to avoid send a request on every keystroke */
export const createDebouncedFilter = (delay = 300) =>
    debounce((e: FilteringEventArgs, datasource: DataManager, searchTerm: string, operator: string) => {
        const query = e.text ? new Query().where(searchTerm, operator, e.text, true) : new Query();
        e.updateData(datasource, query);
    }, delay);
